<script setup lang="ts">
import { ref, onMounted, computed, nextTick } from 'vue';
import { posStore } from '../../../../stores/posStore';
import { Item, ItemPackingPrice } from '../../../../types/models';
import { api } from '../../../../api/Api';
import Toast from '../../../../services/Toast';
import IconComponent from '../../../../components/ui/common/IconComponent.vue';
import { formatCurrency, formatNumber } from '../../../../utils/CommonFunctions';
import ItemPricesForm from '../../../inventory/itemmanagement/itemprices/ItemPricesForm.vue'
import AsyncImage from '../../../../components/ui/form/AsyncImage.vue';

const pos = posStore();
const searchQuery = ref('');
const filteredProducts = ref<Item[]>([]);


const cartItemCount = computed(() => {
  return pos.selectedPosInvoice?.invoice?.items?.length || 0;
});

const getProducts = async () => {
  if (searchQuery.value.length > 0) {
    const params = {
    search_query: searchQuery.value,
    price_lists_id: pos.selectedPosInvoice?.invoice?.price_lists_id,
    warehouses_id: pos.selectedPosInvoice?.invoice?.warehouses_id,
    };
    const response = await api.post(
      "inventory/item-management/items/search",
      params
    );
    if (response.success == true) {
      filteredProducts.value = response.data;
      filteredProducts.value.forEach((el) => {
        if (el.variant_packing_prices && el.variant_packing_prices.length > 0) {
          el.packing_prices = el.variant_packing_prices;
        }
      });
      if (!response.data || response.data.length <= 0) {
        Toast.show("Item not found", "warning");
      }
    } else {
      Toast.show("Item not found", "warning");
    }
  }
};

const isCartExpanded = ref(false);
function toggleCart() {
  isCartExpanded.value = !isCartExpanded.value;
}

function updateQuantity(item: Item, newQuantity: number) {
  pos.updateQuantity(item, newQuantity)
}

function removeItem(item: Item) {
  pos.removeItem(item);
  Toast.show("Item removed from cart", "info");
  if (pos.selectedPosInvoice?.invoice?.items.length === 0) {
    isCartExpanded.value = false;
  }
}


const searchInput = ref<HTMLInputElement | null>(null);

const currentPage = ref(1);
const handlePageChange = (page: number) => {
  currentPage.value += page;
  getProducts();
};

const focusSearch = () => {
  searchQuery.value = "";
  searchInput.value?.focus();
};

const handleProductClick = (product: Item) => {
  pos.addProductToInvoice(product)
  nextTick(() => {
    focusSearch();
  })
};

const showItemDetailForm = ref(false);
const selectedProduct = ref<Item | null>(null);
const displayProductInfo = (product: Item) => {
  selectedProduct.value = product;
  showItemDetailForm.value = true;
  //showProductInfo.value = true;
  //selectedProduct.value = product;
};


const handlePackingClick = (packing: ItemPackingPrice, product: Item) => {
  console.log(packing);
  console.log(product);
  let prod: Item = {
    item_name: product.name + " " + packing.unit_of_measure + 'x' + packing.inventory_qty,
    id: product.id,
    photo: product.photo, //TODO: seperate photo for packing prices  
    code: packing.code, 
    name:  product.name + " " + packing.unit_of_measure + (packing.inventory_qty > 1 ? 'x' + packing.inventory_qty : ''),
    item:  product.name + " " + packing.unit_of_measure + (packing.inventory_qty > 1 ? 'x' + packing.inventory_qty : ''),
    item_type: product.item_type,
    sales_name: product.name + " " + packing.unit_of_measure +  (packing.inventory_qty > 1 ? 'x' + packing.inventory_qty : ''),
    item_types_id: product.item_types_id,
    flexi_price: product.flexi_price,
    ignore_discount: product.ignore_discount,
    tracked_by: product.tracked_by,
    sales_uom: packing.unit_of_measure, 
    sales_uom_qty: packing.inventory_qty, 
    default_sales_uom_id: product.default_sales_uom_id,
    default_sales_uom_qty: product.default_sales_uom_qty,
    is_package: product.is_package, 
    use_package_price: product.use_package_price, 
    is_expiry: product.is_expiry, 
    tax_details_id: product.tax_details_id, 
    inventory_uom_id: product.inventory_uom_id, 
    inventory_uom_qty: product.inventory_uom_qty,
    sales_uom_id: packing.unit_of_measures_id, 
    has_variants: product.has_variants, 
    variants: product.variants, 
    tax: product.tax, 
    tax_rate: product.tax_rate, 
    uid: product.uid, 
    warehouses_id: pos.selectedPosInvoice?.invoice?.warehouses_id, 
    price_lists_id: pos.selectedPosInvoice?.invoice?.price_lists_id, 
    items_id: product.id, 
    item_variants_id: product.item_variants_id, 
    unit_of_measures_id: packing.unit_of_measures_id, 
    unit_of_measure: packing.unit_of_measure, 
    on_hand: product.on_hand, 
    quantity: 1, 
    uom_qty: packing.inventory_qty, 
    inventory_qty: 1 * packing.inventory_qty, 
    cost: product.cost, 
    original_price: packing.selling_price,
    selling_price: packing.selling_price,
    price: packing.selling_price,
    sub_total: packing.selling_price, 
    discount_p: 0, 
    discount_a: 0, 
    value_before_tax: product.selling_price, 
    tax_amount: 0, 
    net_total: 0, 
    remarks: '',
    batches: [],
    serials: [],
    quantity_prices: [],
    packing_prices: [],
    barcodes: [],
    shelfs: [],
    variant_list: [],
    variant_name: product.variant_name,
    showPackingPrices: product.showPackingPrices,
    isFocused: false,
    isHovered: false,
    isExpanded: false
  }
  pos.addProductToInvoice(prod)
  nextTick(() => {
    focusSearch();
  })
};



onMounted(() => {
  getProducts();
});
</script>

<template>
  <div class="sales-step">
    <div class="p-4 h-full overflow-y-auto pb-20">
      <!-- Cart Summary -->
      <div v-if="cartItemCount > 0" class="relative mb-4">
        <div 
          class="card bg-primary-50 border border-primary-200"
          :class="{ 'rounded-b-none border-b-0': isCartExpanded }"
        >
          <div class="flex justify-between items-center">
            <button 
              class="flex items-center flex-1"
              @click="toggleCart"
            >
              <div>
                <h3 class="font-medium text-neutral-900">Cart Summary</h3>
                <p class="text-sm text-neutral-600">{{ cartItemCount }} item{{ cartItemCount !== 1 ? 's' : '' }}</p>
              </div>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                class="h-5 w-5 ml-2 text-neutral-500 transition-transform"
                :class="{ 'rotate-180': isCartExpanded }"
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div class="text-xl font-semibold text-primary-600">{{ formatNumber(pos.net_total) }}</div>
          </div>
        </div>

        <!-- Expanded Cart Details -->
        <div 
          v-if="isCartExpanded"
          class="card rounded-t-none border-t-0 border border-primary-200 bg-white"
        >
          <div class="space-y-4">
            <div 
              v-for="item in pos.selectedPosInvoice?.invoice?.items || []" 
              :key="item.id"
              class="flex items-center space-x-4"
            >
            <AsyncImage :src="item.photo" :alt="item.name" :readonly="true" class-name="max-h-[80px]" />
              
              <div class="flex-1">
                <h4 class="font-medium text-neutral-900">{{ item.name }}</h4>
                <p class="text-sm text-neutral-600">{{ formatNumber(item.price) }} each</p>
              </div>
              
              <div class="flex items-center space-x-2">
                <button 
                  @click="updateQuantity(item, item.quantity - 1)"
                  class="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
                  </svg>
                </button>
                
                <span class="w-8 text-center">{{ item.quantity }}</span>
                
                <button 
                  @click="updateQuantity(item, item.quantity + 1)"
                  class="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                  </svg>
                </button>
                
                <button 
                  @click="removeItem(item)"
                  class="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center text-error-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Search Bar -->
      <div class="p-4 border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center gap-2">
        <div class="relative flex-1">
          <IconComponent name="Search" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="search"
            placeholder="Type and hit Enter"
            @keyup.enter="getProducts"
            class="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
          />
        </div>
      </div>
    </div>
      <div v-if="filteredProducts.length > 0">
        <h2 class="text-lg font-semibold text-neutral-900 mb-3">Select Products</h2>
        
        <div class="flex-1 p-4 overflow-y-auto">
      <div
        class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4"
      >
        <template v-for="product in filteredProducts" :key="product.id">
            <div
            @click="handleProductClick(product)"
            class="w-full max-w-[150px] p-4 rounded-lg transition-colors 
            text-center group cursor-pointer relative"
            :class="{'bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800': Number(product.on_hand) > 0, 
            'bg-red-200 dark:bg-red-700': Number(product.on_hand) <= 0}"
            >
            <div class="absolute top-2 right-2">
              <button
              @click.stop="displayProductInfo(product)"
              class="p-0 transition-colors"
              >
              <IconComponent 
                name="MoreVertical" 
                class="w-5 h-5 text-gray-600 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400" 
              />
              </button>
            </div>
            <div 
              v-if="pos.selectedPosInvoice?.invoice?.items.find(i => i.code === product.code)" 
              class="absolute top-2 right-2 bg-green-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center"
            >
              {{ pos.selectedPosInvoice?.invoice?.items.find(i => i.code === product.code)?.quantity || '' }}
            </div>
            <div
              class="w-16 h-16 mx-auto mb-2 text-3xl flex items-center justify-center"
            >
              <AsyncImage :src="product.photo" :alt="product.name" :readonly="true" class-name="max-h-[80px]" />
            </div>
            <h2 class="font-medium text-gray-900 dark:text-white text-sm mb-1">
              {{ product.code }}
            </h2>
            <h3 class="font-medium text-gray-900 dark:text-white text-sm mb-1 truncate">
              {{ (product.name ?? '').length > 50 ? (product.name ?? '').substring(0, 50) + '...' : (product.name ?? '') }}
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ formatCurrency(product.price) }}
            </p>
            <div class="flex items-center justify-center text-sm text-gray-600 dark:text-gray-400">
              <IconComponent name="Boxes" />
              <span class="ml-1">{{ formatNumber(product.on_hand) }}</span>
            </div>
            </div>
          <template v-if="product.packing_prices">
            <div
            v-for="packing in product.packing_prices.filter(p => p.price_lists_id  == pos.selectedPosInvoice?.invoice?.price_lists_id)"
              :key="product.code ?? '' + packing.code"
              @click="handlePackingClick(packing, product)"
              class="w-full max-w-[150px] p-4 rounded-lg bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-center group relative"
            >
            <div 
            v-if="pos.selectedPosInvoice?.invoice?.items.find(i => i.code === (product.code ?? '') + packing.code)"
              class="absolute top-2 right-2 bg-green-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center"
            >
              {{ pos.selectedPosInvoice?.invoice?.items.find(i => i.code === (product.code ?? '') + packing.code)?.quantity || '' }}
            </div>
            <div
              class="w-16 h-16 mx-auto mb-2 text-3xl flex items-center justify-center"
            >
              <AsyncImage :src="product.photo" :alt="product.name" :readonly="true" class-name="max-h-[80px]" />
            </div>
            <h2 class="font-medium text-gray-900 dark:text-white text-sm mb-1">
              {{ (product.code ?? '') + packing.code }}
            </h2>
            <h3 class="font-medium text-gray-900 dark:text-white text-sm mb-1 truncate">
              {{ (product.name ?? '').length > 50 ? (product.name ?? '').substring(0, 50) + '...' : (product.name ?? '') }} <br />
              {{ packing.unit_of_measure }} x {{ packing.inventory_qty }}
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ formatCurrency(packing.selling_price) }} Pcs
            </p>
            </div>
          </template>
        </template>
      </div>

      <!-- Pagination -->
      <div class="flex justify-center items-center mt-6 space-x-2" v-if="pos.totalPages > 1">
        <button
          @click="handlePageChange(-1)"
          :disabled="currentPage === 1"
          class="px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          Previous
        </button>

        <div class="flex space-x-1">
          <button
            v-for="page in pos.totalPages"
            :key="page"
            @click="handlePageChange(page)"
            class="px-3 py-1 text-sm font-medium rounded"
            :class="[
              currentPage === page
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700',
            ]"
          >
            {{ page }}
          </button>
        </div>

        <button
          @click="handlePageChange(1)"
          :disabled="currentPage === pos.totalPages"
          class="px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>

      </div>

      
      <div v-else class="text-center py-8">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        <p class="mt-3 text-neutral-600">No products found</p>
      </div>
    </div>
    <ItemPricesForm v-if="showItemDetailForm" :data="selectedProduct" mode="view" end-point="inventory/item-management/items"
    @close="showItemDetailForm = false" :ignore-permission="true" :show-inventory="true" title="Item Details" />
  </div>
</template>